import { fmt } from '../format.ts';
import { heatColor, heatCellContinuous } from '../../color.ts';
import type { CrossLobRow, Lob } from '../types.ts';

// View 6 — dimensions × LOB means as a continuously-shaded heatmap (deeper blue = higher
// maturity), plus a Range column (max − min). Every cell prints its mean, so colour is a
// redundant channel (never colour-alone). perLob is keyed by LOB *name* (the backend contract
// shared with the tests and the Excel export); LOB name is unique per engagement.
export function CrossLobView({ data, lobs }: { data: CrossLobRow[]; lobs: Lob[] }) {
  if (lobs.length <= 1) {
    return (
      <p className="text-sm text-ink-soft">
        Cross-LOB comparison becomes available once an engagement has more than one line of business.
      </p>
    );
  }

  const legendGradient = `linear-gradient(to right, ${heatColor(1)}, ${heatColor(2)}, ${heatColor(3)}, ${heatColor(4)}, ${heatColor(5)})`;

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">
            Cross-LOB dimension means, shaded by maturity (deeper = higher). Range = max − min per row.
          </caption>
          <thead>
            <tr className="border-b border-black/10 text-xs uppercase tracking-wide text-ink-soft">
              <th scope="col" className="py-2 pr-4 text-left font-semibold">Dimension</th>
              {lobs.map((lob) => (
                <th key={lob.id} scope="col" className="px-3 py-2 text-right font-semibold">{lob.name}</th>
              ))}
              <th scope="col" className="py-2 pl-3 text-right font-semibold">Range</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.slug} className="border-b border-black/5 last:border-0">
                <th scope="row" className="py-2.5 pr-4 text-left font-medium text-ink">{row.name}</th>
                {lobs.map((lob) => {
                  const value = row.perLob[lob.name] ?? null;
                  return (
                    <td
                      key={lob.id}
                      className="px-3 py-2.5 text-right font-semibold tabular-nums"
                      style={heatCellContinuous(value)}
                      title={`${row.name} · ${lob.name}: ${fmt(value)}`}
                    >
                      {fmt(value)}
                    </td>
                  );
                })}
                <td className="py-2.5 pl-3 text-right font-medium tabular-nums text-ink-soft">{fmt(row.range)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center gap-2 text-xs text-ink-soft">
        <span>Lower</span>
        <span
          className="inline-block h-2.5 w-40 rounded-full ring-1 ring-black/10"
          style={{ background: legendGradient }}
          aria-hidden
        />
        <span>Higher maturity (mean 1–5)</span>
      </div>
    </div>
  );
}
